/**
 * RFC 9457 problem+json, as the frontend sees it.
 *
 * Mirrors `apps/api/src/common/errors/problem-details.ts`. Kept as a hand-written
 * mirror rather than generated, because these are the strings the UI branches
 * on and a silent rename should break the build here too.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.10, §6.2
 */

/** Base URI for API problem types. */
export const PROBLEM_TYPE_BASE = 'https://nexus.greenvolt.com/problems';

/** Every problem type the API can return. */
export const ProblemType = {
  VALIDATION: `${PROBLEM_TYPE_BASE}/validation-failed`,
  UNAUTHENTICATED: `${PROBLEM_TYPE_BASE}/unauthenticated`,
  FORBIDDEN: `${PROBLEM_TYPE_BASE}/forbidden`,
  NOT_FOUND: `${PROBLEM_TYPE_BASE}/not-found`,
  CONFLICT: `${PROBLEM_TYPE_BASE}/conflict`,
  VERSION_CONFLICT: `${PROBLEM_TYPE_BASE}/version-conflict`,
  TERM_IN_USE: `${PROBLEM_TYPE_BASE}/taxonomy-term-in-use`,
  /** A stage's exit criteria are unmet and were not acknowledged (PRD §5.3). */
  EXIT_CRITERIA_UNMET: `${PROBLEM_TYPE_BASE}/exit-criteria-unmet`,
  PAYLOAD_TOO_LARGE: `${PROBLEM_TYPE_BASE}/payload-too-large`,
  UNSUPPORTED_MEDIA_TYPE: `${PROBLEM_TYPE_BASE}/unsupported-media-type`,
  RATE_LIMITED: `${PROBLEM_TYPE_BASE}/rate-limited`,
  INTERNAL: `${PROBLEM_TYPE_BASE}/internal-error`,
} as const;

export type ProblemTypeValue = (typeof ProblemType)[keyof typeof ProblemType];

/** One field-level validation failure. */
export interface FieldError {
  readonly field: string;
  readonly message: string;
  readonly code?: string;
}

/** The API's error body. */
export interface ProblemDetails {
  readonly type: string;
  readonly title: string;
  readonly status: number;
  readonly detail?: string;
  readonly instance?: string;
  readonly requestId?: string;
  readonly timestamp?: string;
  readonly errors?: readonly FieldError[];
  /** The version the caller submitted, on a version conflict. */
  readonly expectedVersion?: number;
  /** Present on an unmet-criteria 422: what the client shows before asking. */
  readonly warnings?: readonly string[];
}

/**
 * An API call that failed.
 *
 * Carries the parsed problem body so a caller can branch on `type` rather than
 * matching message text — the reason the API emits a stable `type` at all.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails;

  constructor(problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.status = problem.status;
    this.problem = problem;
  }

  /** True when this is the given problem type. */
  is(type: ProblemTypeValue): boolean {
    return this.problem.type === type;
  }

  /** Field errors, keyed by field, for attaching to form inputs. */
  get fieldErrors(): Record<string, string> {
    const result: Record<string, string> = {};
    for (const error of this.problem.errors ?? []) {
      result[error.field] ??= error.message;
    }
    return result;
  }
}

/** Type guard for {@link ApiError}. */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Builds a problem body for a failure that never reached the API.
 *
 * A network error has no response to parse, but callers should still receive
 * one shape rather than having to distinguish `TypeError` from `ApiError`.
 */
export function networkProblem(detail: string): ProblemDetails {
  return {
    type: ProblemType.INTERNAL,
    title: 'Network error',
    status: 0,
    detail,
  };
}

/**
 * Parses a response body into a problem.
 *
 * Falls back to a synthetic problem when the body is not problem+json — a
 * proxy timeout or a crashed process returns HTML, and the UI still needs a
 * status and a title.
 */
export async function parseProblem(response: Response): Promise<ProblemDetails> {
  try {
    const body: unknown = await response.json();

    if (typeof body === 'object' && body !== null && 'type' in body && 'status' in body) {
      return body as ProblemDetails;
    }
  } catch {
    /* Not JSON. Fall through to the synthetic problem below. */
  }

  return {
    type: ProblemType.INTERNAL,
    title: response.statusText === '' ? 'Request failed' : response.statusText,
    status: response.status,
  };
}
